/**
 * Minimal JSON Schema subset validator + form-field deriver for event
 * payload schemas produced by `z.toJSONSchema()` on the server.
 *
 * Deliberately NOT a full draft-2020 validator (no ajv in this repo, and
 * the plugin payload models only use `type` / `properties` / `required` /
 * `enum` / `items` / `anyOf`-of-types). Unknown keywords are tolerated —
 * validation here is a UX gate for the run dialog, the server re-validates
 * on submit. If full JSON Schema support is ever needed, swap this file
 * for ajv behind the same function signatures.
 */

export interface JsonSchemaIssue {
  /** Dotted path into the document ('' for the root). */
  path: string;
  message: string;
}

type Schema = Record<string, unknown>;

export function validateJsonAgainstSchema(
  schema: Record<string, unknown> | null | undefined,
  value: unknown,
): JsonSchemaIssue[] {
  if (!schema) return [];
  const issues: JsonSchemaIssue[] = [];
  validateNode(schema, value, '', issues);
  return issues;
}

function typeOf(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function matchesType(expected: string, value: unknown): boolean {
  const actual = typeOf(value);
  if (expected === 'integer') return actual === 'number' && Number.isInteger(value);
  return expected === actual;
}

function validateNode(schema: Schema, value: unknown, path: string, issues: JsonSchemaIssue[]) {
  // anyOf-of-subschemas: pass when any branch passes.
  const anyOf = schema.anyOf;
  if (Array.isArray(anyOf) && anyOf.length > 0) {
    const passesAny = anyOf.some(
      (sub) =>
        sub &&
        typeof sub === 'object' &&
        validateJsonAgainstSchema(sub as Schema, value).length === 0,
    );
    if (!passesAny) {
      issues.push({ path, message: 'does not match any of the allowed shapes' });
    }
    return;
  }

  const expectedType = schema.type;
  if (typeof expectedType === 'string' && value !== undefined) {
    if (!matchesType(expectedType, value)) {
      issues.push({ path, message: `expected ${expectedType}, got ${typeOf(value)}` });
      return;
    }
  } else if (Array.isArray(expectedType) && value !== undefined) {
    if (!expectedType.some((t) => typeof t === 'string' && matchesType(t, value))) {
      issues.push({ path, message: `expected ${expectedType.join(' | ')}, got ${typeOf(value)}` });
      return;
    }
  }

  const enumValues = schema.enum;
  if (Array.isArray(enumValues) && value !== undefined && !enumValues.includes(value)) {
    issues.push({
      path,
      message: `must be one of: ${enumValues.map((v) => JSON.stringify(v)).join(', ')}`,
    });
    return;
  }

  if (typeOf(value) === 'object') {
    const record = value as Record<string, unknown>;
    const required = Array.isArray(schema.required) ? (schema.required as string[]) : [];
    for (const key of required) {
      if (record[key] === undefined || record[key] === null || record[key] === '') {
        issues.push({ path: joinPath(path, key), message: 'is required' });
      }
    }
    const properties = schema.properties;
    if (properties && typeof properties === 'object') {
      for (const [key, sub] of Object.entries(properties as Record<string, unknown>)) {
        if (record[key] === undefined || !sub || typeof sub !== 'object') continue;
        validateNode(sub as Schema, record[key], joinPath(path, key), issues);
      }
    }
    // Extra keys beyond `properties` are always allowed — rich webhook
    // payloads carry more than the declared model.
  }

  if (Array.isArray(value)) {
    const items = schema.items;
    if (items && typeof items === 'object' && !Array.isArray(items)) {
      value.forEach((item, i) => validateNode(items as Schema, item, `${path}[${i}]`, issues));
    }
  }
}

function joinPath(path: string, key: string): string {
  return path ? `${path}.${key}` : key;
}

// --- Form field derivation ---

export interface FormFieldDescriptor {
  name: string;
  label: string;
  /** `json` fields render as a mini JSON textarea (nested objects/arrays). */
  type: 'string' | 'text' | 'number' | 'boolean' | 'enum' | 'json';
  required: boolean;
  enumValues?: string[];
  description?: string;
  /** Friendly display label (JSON Schema `title`), e.g. 'Assignee'. */
  title?: string;
}

/** The resolved type + enum of a single field, for typing filter values. */
export interface ResolvedFieldSchema {
  type: 'string' | 'number' | 'boolean' | 'enum' | 'json';
  enumValues?: string[];
  /** Semantic field type (e.g. 'linear:user') keying a field-option provider. */
  fieldType?: string;
  /** Friendly display label (JSON Schema `title`), e.g. 'Assignee'. */
  title?: string;
}

/**
 * Resolve the type (and enum, when present) of a dotted `path` into a payload
 * schema. `rootPrefix` (e.g. `trigger`) is stripped first — filter condition
 * paths are authored as `trigger.<field>` against the event payload. Descends
 * `properties` for object segments and `items` for array segments; returns
 * `undefined` when any segment can't be resolved so callers fall back to a
 * free-text input.
 */
export function resolveFieldSchemaAtPath(
  schema: Record<string, unknown> | null | undefined,
  path: string,
  rootPrefix?: string,
): ResolvedFieldSchema | undefined {
  if (!schema) return undefined;
  const segments = path.split('.').filter(Boolean);
  if (rootPrefix) {
    if (segments[0] !== rootPrefix) return undefined;
    segments.shift();
  }
  if (segments.length === 0) return undefined;

  let current: Schema | undefined = schema;
  for (const segment of segments) {
    if (!current) return undefined;
    let node = current;
    // Step into array element schemas transparently (e.g. `labels.0.name`).
    while (node && node.type === 'array' && node.items && typeof node.items === 'object') {
      node = node.items as Schema;
    }
    const properties = node.properties;
    if (!properties || typeof properties !== 'object') return undefined;
    const next = (properties as Record<string, unknown>)[segment];
    if (!next || typeof next !== 'object') return undefined;
    current = next as Schema;
  }

  return describeFieldSchema(current);
}

function describeFieldSchema(prop: Schema | undefined): ResolvedFieldSchema | undefined {
  if (!prop) return undefined;
  // Carried on every branch so a field whose type resolution degrades (e.g.
  // an anyOf) still keys its option provider and friendly label.
  const meta: Pick<ResolvedFieldSchema, 'fieldType' | 'title'> = {
    ...(typeof prop.fieldType === 'string' ? { fieldType: prop.fieldType } : {}),
    ...(typeof prop.title === 'string' ? { title: prop.title } : {}),
  };
  const enumValues = Array.isArray(prop.enum)
    ? (prop.enum as unknown[]).filter((v): v is string => typeof v === 'string')
    : undefined;
  if (enumValues && enumValues.length > 0) return { type: 'enum', enumValues, ...meta };
  switch (prop.type) {
    case 'string':
      return { type: 'string', ...meta };
    case 'number':
    case 'integer':
      return { type: 'number', ...meta };
    case 'boolean':
      return { type: 'boolean', ...meta };
    default:
      return { type: 'json', ...meta };
  }
}

/**
 * Map a payload schema's top-level properties onto run-dialog form fields.
 * Nested objects and arrays render as `json` textareas; everything fancier
 * lives in the dialog's JSON paste tab.
 */
export function jsonSchemaToFormFields(
  schema: Record<string, unknown> | null | undefined,
): FormFieldDescriptor[] {
  if (!schema) return [];
  const properties = schema.properties;
  if (!properties || typeof properties !== 'object') return [];
  const required = new Set(Array.isArray(schema.required) ? (schema.required as string[]) : []);

  return Object.entries(properties as Record<string, unknown>).map(([name, raw]) => {
    const prop = raw && typeof raw === 'object' ? (raw as Schema) : {};
    const description = typeof prop.description === 'string' ? prop.description : undefined;
    const title = typeof prop.title === 'string' ? prop.title : undefined;
    const enumValues = Array.isArray(prop.enum)
      ? (prop.enum as unknown[]).filter((v): v is string => typeof v === 'string')
      : undefined;

    let type: FormFieldDescriptor['type'];
    if (enumValues && enumValues.length > 0) {
      type = 'enum';
    } else {
      switch (prop.type) {
        case 'string':
          type = 'string';
          break;
        case 'number':
        case 'integer':
          type = 'number';
          break;
        case 'boolean':
          type = 'boolean';
          break;
        default:
          type = 'json';
      }
    }

    return {
      name,
      label: name,
      type,
      required: required.has(name),
      ...(enumValues && enumValues.length > 0 ? { enumValues } : {}),
      ...(description ? { description } : {}),
      ...(title ? { title } : {}),
    };
  });
}
